/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLazyFetchBotQuery } from '../../../store/bots/botsApi';
import { useEffect } from 'react';

/**
 * Hook to manage bot language
 * @param {string} botId The bot id - if not provided, it will be taken from the URL (so it should be provided in cases where the user is not on the bot page)
 * @returns {{botLanguages: string[], primaryBotLanguage: string, currentBotLanguage: string, isEditingPrimaryLanguage: boolean, changeBotLanguage: (function(*=): void)}}
*/
export const useBotLanguage = (botId) => {
  const location = useLocation();
  const { orgId, tenantId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const [fetchBot, { data: bot }] = useLazyFetchBotQuery();
  const primaryBotLanguage = bot?.settings?.languages?.primary || 'en';
  const currentBotLanguage = searchParams.get('contentLang') || primaryBotLanguage;
  const isEditingPrimaryLanguage = currentBotLanguage === primaryBotLanguage;
  const botLanguages = primaryBotLanguage ? [...new Set([primaryBotLanguage, ...bot?.settings?.languages?.additional || []])] : [];

  const changeBotLanguage = newLang => {
    searchParams.set('contentLang', newLang || primaryBotLanguage);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };

  useEffect(() => {
    if (orgId && (tenantId || botId)) {
      fetchBot({ orgId, tenantId: botId || tenantId }, true);
    }
  }, [orgId, tenantId, botId]);

  return {
    botLanguages,
    primaryBotLanguage,
    currentBotLanguage,
    isEditingPrimaryLanguage,
    changeBotLanguage
  };
};
